import React, { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Loader2, Instagram, LogOut, AlertTriangle, Layers } from 'lucide-react';

export const MetaConnectButton: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [accountsCount, setAccountsCount] = useState(0);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkStatus();

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkStatus = async () => {
    try {
      setLoading(true);
      const res = await api.getMetaStatus();
      setConnected(res.connected);
      setUserName(res.user_name);
      setAccountsCount(res.instagram_accounts_count || res.accounts_count);
      setAccounts(res.accounts || []);
    } catch (err) {
      console.warn('No se pudo verificar el estado de Meta:', err);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setErrorMessage(null);
      const redirectUri = window.location.origin + '/auth/meta/callback';
      const res = await api.getMetaAuthUrl(redirectUri);

      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      console.error('Error al iniciar OAuth con Meta:', err);
      setErrorMessage(
        err.message ||
          'Configura META_APP_ID y META_APP_SECRET en el Worker para conectar con Facebook e Instagram.'
      );
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.disconnectMeta();
      setConnected(false);
      setUserName(null);
      setAccountsCount(0);
      setAccounts([]);
      setMenuOpen(false);
    } catch (err) {
      console.error('Error al desconectar Meta:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-card/60 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>Meta...</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      {connected ? (
        <div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-sky-200/80 bg-gradient-to-r from-sky-50 to-indigo-50/60 hover:from-sky-100/70 hover:to-indigo-100/70 text-sky-900 text-xs font-medium transition-all shadow-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
            </span>
            <Instagram className="w-3.5 h-3.5 text-pink-600" />
            <span className="hidden sm:inline">Meta conectado</span>
            <span className="px-1.5 py-0.2 rounded-full bg-sky-200/70 text-sky-800 text-[10px] font-bold">
              {accountsCount} {accountsCount === 1 ? 'cuenta' : 'cuentas'}
            </span>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-72 rounded-xl bg-white border border-slate-200/80 shadow-lg p-3 z-50 animate-in fade-in slide-in-from-top-1 text-slate-800">
              <div className="flex items-center gap-2.5 pb-2.5 mb-2 border-b border-slate-100">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                  <Instagram className="w-4 h-4" />
                </div>
                <div className="overflow-hidden">
                  <p className="text-xs font-semibold text-slate-900 truncate">
                    {userName || 'Teresita'}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    Meta Business Suite activo
                  </p>
                </div>
              </div>

              <div className="mb-2">
                <p className="text-[11px] font-semibold text-slate-500 mb-1.5 px-1 flex items-center gap-1.5">
                  <Layers className="w-3 h-3 text-slate-400" />
                  <span>Cuentas vinculadas ({accounts.length}):</span>
                </p>
                <div className="max-h-36 overflow-y-auto space-y-1">
                  {accounts.map((acc) => (
                    <div
                      key={acc.id}
                      className="px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-xs flex items-center justify-between"
                    >
                      <div className="truncate mr-2">
                        <p className="font-medium text-slate-800 truncate">{acc.name}</p>
                        {acc.instagram_business_account?.username && (
                          <p className="text-[10px] text-pink-600 truncate">
                            @{acc.instagram_business_account.username}
                          </p>
                        )}
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold shrink-0">
                        IG Activo
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleDisconnect}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-100 transition-colors mt-2"
              >
                <LogOut className="w-3 h-3" />
                <span>Desconectar Meta</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-end">
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 active:scale-[0.98] text-slate-700 text-xs font-medium shadow-sm transition-all"
            title="Conectar tu Facebook y cuentas de Instagram de clientes"
          >
            {connecting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
                <span>Abriendo Meta...</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    fill="#1877F2"
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
                <span>Conectar Meta / Instagram</span>
              </>
            )}
          </button>

          {errorMessage && (
            <div className="absolute right-0 top-10 mt-1 w-72 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-[11px] shadow-lg z-50 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Faltan credenciales de Meta</p>
                <p className="text-[10px] text-amber-700 mt-0.5 leading-snug">{errorMessage}</p>
                <button
                  onClick={() => setErrorMessage(null)}
                  className="mt-1 text-[10px] text-amber-900 underline font-medium"
                >
                  Entendido
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
